import { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Button, TextField } from '@mui/material';
import CustomDialog from '../../../common/CustomDialog';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { FlexBetween, FlexEnd } from '../../../../commonStyles';
import { convertAllDates } from '../../../../utils/Functions';
import { UnitContext } from '../../../../context/UnitContext';
import { useAppDispatch } from '../../../../app/hooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../../../features/snackbar/snackbarSlice';
import { accessAllowed } from '../../../../features/project/projectHooks';
import { selectUser } from '../../../../features/auth/authSlice';
import { selectProject } from '../../../../features/project/projectSlice';
import { IDocumentData, IPdfTemplate, ISigner } from '../../../../types/boldSign';
import { IPurchaserInfo } from '../../../../types/CreateDealForm';
import { useCreateActivity } from '../../../../features/activity/activityHooks';
import { IDealRealtor } from '../../../../types/CreateDealForm';

const EnvelopeAccordion = (props: ChildProps) => {
  const createActivity = useCreateActivity();
  const storeDispatch = useAppDispatch();
  const { doc, sendingDocumentToExecutor, openDialog } = props;
  const user = useSelector(selectUser);
  const project = useSelector(selectProject);
  const { filteredDeal, setFilteredDeal } = useContext(UnitContext);
  const [documentData, setDocumentData] = useState<IDocumentData | null>(null);
  const [pdfTemplate, setPdfTemplate] = useState<IPdfTemplate | null>(null);
  const [templateLoading, setTemplateLoading] = useState<boolean>(false);
  const [renameOpen, setRenameOpen] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>('');

  const [renameDocument, { loading: renameLoading }] = useMutation(RENAMEDOCUMENT, {
    onCompleted: (data) => {
      const name = data.documentUpdateById.record.name;
      // Keep the Documents list in sync without a reload
      setFilteredDeal((current: any) => ({
        ...current,
        documents: current.documents.map((document: any) => (document._id === doc._id ? { ...document, name } : document)),
      }));
      createActivity({
        project: project._id,
        user: user._id,
        deal: filteredDeal._id,
        title: `Document Renamed`,
        content: `Document ${doc.name} has been renamed to ${name}`,
      });
      storeDispatch(showSuccessSnackbar('Document has been renamed!'));
      setRenameOpen(false);
    },
    onError: (err) => {
      console.log(err, 'err');
      storeDispatch(showErrorSnackbar('Unable to rename the document'));
    },
  });

  // Characters that aren't allowed in file names (the document name is used for downloaded files)
  const INVALID_NAME_CHARACTERS = /[<>:"/\\|?*]/;
  const nameHasInvalidCharacters = INVALID_NAME_CHARACTERS.test(newName);

  const openRename = () => {
    setNewName(doc.name);
    setRenameOpen(true);
  };

  const saveRename = () => {
    const name = newName.trim();
    if (!name) {
      storeDispatch(showErrorSnackbar('Enter a document name'));
      return;
    }
    if (INVALID_NAME_CHARACTERS.test(name)) {
      storeDispatch(showErrorSnackbar('The name cannot contain < > : " / \\ | ? *'));
      return;
    }
    if (name === doc.name) {
      setRenameOpen(false);
      return;
    }
    renameDocument({ variables: { _id: doc._id, record: { name } } });
  };

  useEffect(() => {
    if (pdfTemplate) {
      const receiveMessage = (event: any) => {
        if (event.origin !== 'https://app-ca.boldsign.com') {
          return;
        } else {
          if (event.data.status === 'onCreateSuccess' && event.data.action === 'TemplateCreate') {
            window.removeEventListener("message", receiveMessage, false);
            setPdfTemplate(null);
            reviewDocument(true);
          }
        }
      }
  
      window.addEventListener("message", receiveMessage, false);
  
      return () => {
        window.removeEventListener("message", receiveMessage, false);
      }
    }
  }, [pdfTemplate]);

  const createTemplate = async () => {
    setTemplateLoading(true);
    let iframeEle: any = document.getElementById('prepare_page');
    function onCreateClick() {
      iframeEle?.contentWindow.postMessage('onCreateClick', '*');
    }
    await onCreateClick();
  };

  const [sendDocument, { loading: sendDocumentLoading }] = useMutation(SENDDOCUMENT, {
    onCompleted: (data) => {
      createActivity({
        project: project._id,
        user: user._id,
        deal: filteredDeal._id,
        title: `Document Sent!`,
        content: `Document ${data.documentCreateEnvelope.name} has been sent!`,
      });
      setPdfTemplate(null);
      setTemplateLoading(false);
      storeDispatch(showSuccessSnackbar(`Document has been sent!`));
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar(err.toString()));
      console.log(err, 'err');
    },
  });

  const [savePdf, { loading: reviewLoading }] = useMutation(SAVEPDF, {
    onCompleted: (data) => {
      setPdfTemplate(data.savePdf);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [getDocument, { loading: previewLoading }] = useLazyQuery(GETDOCUMENT, {
    fetchPolicy: 'no-cache',
    onCompleted: async (data) => {
      setDocumentData(data.getDocument);
      const response = await fetch(data.getDocument.url);
      const blob2 = await response.blob();
      const url = window.URL.createObjectURL(blob2);
      const element = document.createElement('a');
      element.download = `${data.getDocument.name}.pdf`;
      element.href = url;
      element.click();
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [resendDocument, { loading: resendLoading }] = useLazyQuery(RESENDDOCUMENT, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`Document Reminder has been sent!`));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const previewDocument = (doc: any) => {
    getDocument({
      variables: {
        project: project._id,
        document: doc._id,
      },
    });
  };

  const reviewDocument = async (template: boolean) => {
    let dealRealtorId = null;
    let selectedRealtor = filteredDeal.realtor.find((realtor: IDealRealtor) => doc.name.includes(`${realtor.fullName}`));

    if (selectedRealtor && (doc.type === 'Coop' || doc.type === 'Other')) {
      dealRealtorId = selectedRealtor._id;
    }

    let purchasers = [
      ...filteredDeal.purchasers.map((purchaser: IPurchaserInfo) => purchaser._id),
      ...filteredDeal.pendingAddPurchasers.map((purchaser: IPurchaserInfo) => purchaser._id),
      ...filteredDeal.pendingDeletePurchasers.map((purchaser: IPurchaserInfo) => purchaser._id),
    ];

    if (pdfTemplate) {
      sendDocument({
        variables: {
          documentId: doc._id,
          templateId: pdfTemplate.templateId,
          dealRealtorId: dealRealtorId,
        },
      });
    } else {
      if (!template) {
        await savePdf({
          variables: {
            project: project._id,
            unit: filteredDeal.unit._id,
            document: doc._id,
            purchaserIds: purchasers,
            realtor: dealRealtorId,
            mergeTemplate: null,
            floorplan: null,
            additional: doc.mergeTemplate,
            executor: true,
            aps: false,
          },
        });
      }
    }
  };

  return (
    <div>
      <CustomDialog
        handleClose={() => setRenameOpen(false)}
        handleCloseRemove={() => setRenameOpen(false)}
        handleCloseSuccess={saveRename}
        open={renameOpen}
        removeButton={'Cancel'}
        successButton={'Rename'}
        successDisabled={renameLoading || nameHasInvalidCharacters}
        dialogTitle={'Rename Document'}
        dialogContent={
          <Box>
            <Box sx={{ mb: 2 }}>
              Current name: <strong>{doc.name}</strong>
            </Box>
            <TextField
              autoFocus
              fullWidth
              label="New name"
              value={newName}
              error={nameHasInvalidCharacters}
              helperText={nameHasInvalidCharacters ? 'The name cannot contain < > : " / \\ | ? *' : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveRename();
              }}
            />
          </Box>
        }
      />
      {doc.status !== 'Prepared' ? (
        <Box
          sx={{
            mb: 2,
            width: '100%',
          }}
        >
          {documentData ? (
            <Box>
              <Box sx={{ mb: 2 }}>
                Document Completed: <strong>{doc.completedAt ? convertAllDates(doc.completedAt, 'PPpp') : 'N/A'}</strong>
              </Box>
              {documentData.signers.map((signer: ISigner) => {
                return (
                  <Box sx={{ mb: 2 }}>
                    <strong>{signer.name ? signer.name : 'Executor'}</strong>
                    <Box>
                      <Box>
                        Opened: <strong>{signer.openedAt ? convertAllDates(signer.openedAt, 'PPpp') : 'N/A'}</strong>
                      </Box>
                      <Box>
                        Completed: <strong>{signer.completedAt ? convertAllDates(signer.completedAt, 'PPpp') : 'N/A'}</strong>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}
          <FlexBetween sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              <Button sx={{ mr: 1, fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }} onClick={() => previewDocument(doc)} color="primary" variant="contained">
                {previewLoading ? <CircularProgress size={30} color="secondary" /> : 'Preview Document'}
              </Button>
              {/* Only Completed documents can be renamed */}
              {doc.status === 'Completed' && accessAllowed(user, project._id, 'editDeals') ? (
                <Button
                  sx={{ mr: 1, fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                  onClick={() => openRename()}
                  color="primary"
                  variant="contained"
                >
                  Rename Document
                </Button>
              ) : null}
              {doc.status !== 'Completed' ? 
                <Button
                  sx={{ mr: 1, fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                  onClick={() => resendDocument({ variables: { _id: doc._id } })}
                  color="primary"
                  variant="contained"
                >
                  {resendLoading ? <CircularProgress size={30} color="secondary" /> : 'Resend Document'}
                </Button>
              : null}
            </Box>
            {!filteredDeal.cancelled.dateCancelled && !filteredDeal.draft && !filteredDeal.rescission.datedRescinded ? (
              <FlexEnd>
                {(doc.status === 'Signed' || doc.status === 'Verified') &&
                accessAllowed(user, project._id, 'editDeals') &&
                !filteredDeal.cancelled.dateCancelled &&
                !filteredDeal.rescission.dateRescinded ? (
                  <Button sx={{ mr: 1, fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }} onClick={() => openDialog('execute', doc)} color="success" variant="contained">
                    {sendingDocumentToExecutor ? (
                      <CircularProgress size={24} color="secondary" />
                    ) : (
                      `Send to ${doc.status === 'Verified' ? 'Different' : ''} Executor`
                    )}
                  </Button>
                ) : null}
                {accessAllowed(user, project._id, 'editDeals') &&
                !filteredDeal.cancelled.dateCancelled &&
                !filteredDeal.rescission.dateRescinded ? (
                  <>
                    {!doc.dsEnvelopeId ||
                    (doc.status === 'Completed' && !doc.dsEnvelopeId) ||
                    (!doc.isAPS && doc.status !== 'Completed') ? (
                      <>
                        <Button
                          sx={{ fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                          onClick={() => openDialog('deleteDocument', doc)}
                          color="error"
                          variant="contained"
                        >
                          {'Void and Delete Document'}
                        </Button>
                        {doc.status === 'Draft' ? (
                          <Button
                            sx={{ fontSize: '0.7rem', ml: 2, '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                            onClick={() => openDialog('sendEnvelope', doc)}
                            color="success"
                            variant="contained"
                          >
                            {'Send Draft'}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : null}
              </FlexEnd>
            ) : null}
          </FlexBetween>
        </Box>
      ) : (
        <Box
          sx={{
            mb: 2,
            width: '100%',
          }}
        >
          <div>
            <div>This document has not been sent out yet</div>
          </div>
          {pdfTemplate && pdfTemplate.createUrl ? (
            <Box sx={{ height: '768px', width: '100%', mt: 2 }}>
              <iframe
                sandbox="allow-same-origin allow-scripts allow-forms"
                id="prepare_page"
                src={pdfTemplate?.createUrl}
                height="100%"
                width="100%"
                style={{ border: 0, height: '100%' }}
                title="pdfTemplate"
              />
            </Box>
          ) : null}
          <FlexBetween sx={{ mt: 2 }}>
            <div>
              <Button sx={{ fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }} onClick={() => previewDocument(doc)} color="primary" variant="contained">
                {previewLoading ? <CircularProgress size={30} color="secondary" /> : 'Preview Document'}
              </Button>
            </div>
            {accessAllowed(user, project._id, 'editDeals') &&
            !filteredDeal.cancelled.dateCancelled &&
            !filteredDeal.rescission.dateRescinded ? (
              <FlexEnd>
                <FlexBetween>
                  <Button
                    sx={{ mr: 1, fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                    onClick={() => {
                      window.removeEventListener('message', () => {});
                      openDialog('deleteDocument', doc);
                    }}
                    color="error"
                    variant="contained"
                  >
                    Delete Document
                  </Button>
                </FlexBetween>
                <Button
                  sx={{ fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                  onClick={() => (pdfTemplate ? createTemplate() : reviewDocument(false))}
                  disabled={reviewLoading || templateLoading}
                  color="success"
                  variant="contained"
                >
                  {reviewLoading || templateLoading ? <CircularProgress size={14} style={{ color: '#fff' }} /> : !pdfTemplate ? 'Review Document' : 'Send'}
                </Button>
              </FlexEnd>
            ) : null}
          </FlexBetween>
        </Box>
      )}
    </div>
  );
};

const GETDOCUMENT = gql`
  query getDocument($project: MongoID!, $document: MongoID!) {
    getDocument(project: $project, document: $document) {
      url
      name
      createdAt
      status
      completedAt
      signers {
        name
        completedAt
        status
        openedAt
      }
    }
  }
`;

const RENAMEDOCUMENT = gql`
  mutation documentUpdateById($_id: MongoID!, $record: UpdateByIdDocumentInput!) {
    documentUpdateById(_id: $_id, record: $record) {
      record {
        _id
        name
      }
    }
  }
`;

const SAVEPDF = gql`
  mutation savePdf(
    $project: MongoID!
    $unit: MongoID!
    $file: Upload
    $document: MongoID
    $purchaserIds: [MongoID]
    $realtor: MongoID
    $mergeTemplate: MongoID
    $floorplan: MongoID
    $additional: [MongoID]
    $executor: Boolean
    $aps: Boolean!
  ) {
    savePdf(
      project: $project
      unit: $unit
      file: $file
      document: $document
      purchaserIds: $purchaserIds
      realtor: $realtor
      mergeTemplate: $mergeTemplate
      floorplan: $floorplan
      additional: $additional
      executor: $executor
      aps: $aps
    ) {
      templateId
      createUrl
    }
  }
`;

const SENDDOCUMENT = gql`
  mutation documentCreateEnvelope($documentId: MongoID!, $dealRealtorId: MongoID, $templateId: String) {
    documentCreateEnvelope(documentId: $documentId, dealRealtorId: $dealRealtorId, templateId: $templateId) {
      name
      _id
      status
      dsEnvelopeId
    }
  }
`;

const RESENDDOCUMENT = gql`
  query resendEnvelope($_id: MongoID!) {
    resendEnvelope(_id: $_id)
  }
`;

interface ChildProps {
  doc: any;
  sendingDocumentToExecutor: boolean;
  openDialog: any;
}

export default EnvelopeAccordion;
